<template>
    <div
        ref="container"
        class="eq-form-layout"
        :class="{
            'label-align-left': props.labelAlign === 'left',
            'label-align-center': props.labelAlign === 'center',
            'label-align-right': props.labelAlign === 'right',
            'h-100': props.fillHeight,
        }"
    >
        <slot></slot>
    </div>
</template>
<script setup lang="ts">
import { useHelper } from '@/composeables';
import { useDebouncer } from '@/utilities/Debouncer';
import { useMutationObserver } from '@vueuse/core';
import { ref, watch } from 'vue';
import { onMounted } from 'vue';

const props = withDefaults(
    defineProps<{
        /**
         * The width of the labels
         * This will try to make all labels the same width with a minimum of 120px, but it will adjust to the largest label
         * @default 120 px
         */
        labelWidth?: number;

        /**
         * The alignment of the labels
         * @default 'right'
         */
        labelAlign?: 'left' | 'right' | 'center';
        fillHeight?: boolean;
    }>(),
    {
        labelWidth: 160,
        labelAlign: 'right',
    },
);

const container = ref<HTMLDivElement>();

watch(
    () => container.value,
    () => {
        resizeLabels();
    },
);

const debounce = useDebouncer();

/**
 * Resize the labels
 */
const resizeLabels = () => {
    if (!container.value) return;
    // we need to get this up front and not in the debounce, because the container might be changed in the meantime
    const rows = container.value!.querySelectorAll('.eq-form-row');
    debounce(() => useHelper().resizeLabel(rows, props.labelWidth), 10);
};

/**
 * We need to calculate the width of the labels to make sure they are aligned
 * This is done by finding the largest label and setting all labels to that size
 */
onMounted(() => {
    // We need to listen for DOM changes within the container
    useMutationObserver(container.value!, () => resizeLabels(), { childList: true, subtree: true });

    resizeLabels();
});

defineExpose({
    resizeLabels,
});
</script>
